/**
 * 監査ログの画面。**顧客向けとオペレーター向けで同じものを使う**。
 *
 * 設計の「同じ 1 本のデータを絞って見せる」(rules.md §4.4)をそのまま形にした。
 * 別の監査ログを作らない。
 *
 * ## 出さないもの
 *
 * - 生のユーザー ID と発信元 IP。**API の型に無い**ので、出したくても出せない
 * - 受付番号(requestId)と対象の内部 ID。見出しには置かず、開いたときだけ出す。
 *   人がこの番号で問い合わせる口はまだ無いが、問い合わせを受けた側が突き合わせる
 *   材料にはなるので、捨てはしない
 * - 「何件中の何件」。総件数は数えていない(月ごとの台帳を遡る形なので数えられない)
 *
 * ## 0 件と「読めない」を言い分ける
 *
 * 記録がまだ無いのと、台帳が読めなかったのは別のことである。前者は
 * 「まだ記録がありません」、後者は読み込みの失敗として出す。空の一覧を
 * 「異常なし」と読ませない。
 *
 * ## テナント横断は出せない
 *
 * 台帳の pk にテナントが必ず入るので(NFR6)、全テナントぶんを 1 回で読む
 * 経路が無い。オペレーター向けでも「いま見ているテナントのぶん」になる。
 * その断りは `note` で受け取り、画面の上に出す。
 */

import { useState, type ReactNode } from "react";
import { useQuery } from "urql";

import { formatJstDateTime } from "../format";
import {
  AuditEventsDocument,
  type AuditEventKind,
} from "../graphql/generated/graphql";
import {
  Button,
  Card,
  Disclosure,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Tag,
} from "./primitives";

/**
 * 種別の表示名。**内部の語彙をそのまま画面に出さない**。
 *
 * 語彙に無い種別が来たら、その行は種別を「その他の操作」と出す(英語の定数を
 * 見出しに並べない)。何が起きたかは `summary` に日本語で入っている。
 */
const KIND_LABELS: Record<string, string> = {
  RULE_STATE_CHANGED: "ルールの状態を変更",
  RULE_VERSION_SAVED: "ルールの版を保存",
  RULE_PUBLISHED_VERSION_CHANGED: "配信中の版を変更",
  RULE_DRAFT_UPDATED: "ルールの下書きを更新",
  QUESTION_TEMPLATE_TOGGLED: "想定質問の有効・無効を変更",
  QUESTION_TEMPLATE_ADDED: "想定質問を追加",
  PUBLISH_TARGET_CHANGED: "公開の対象を変更",
  PUBLISH_REFRESH_STARTED: "公開の作り直しを開始",
  API_KEY_CREATED: "API キーを発行",
  API_KEY_REVOKED: "API キーを失効",
  ALLOWED_ORIGINS_CHANGED: "許可するドメインを変更",
  AI_CONNECTION_ISSUED: "AI 接続を発行",
  AI_CONNECTION_STATUS_CHANGED: "AI 接続の状態を変更",
  AI_SHARE_SCOPE_CHANGED: "AI へ渡す範囲を変更",
  AI_OPERATION_CHANGED: "AI から呼べる操作を変更",
  MEMBER_INVITED: "メンバーを招待",
  MEMBER_ROLE_CHANGED: "メンバーの役割を変更",
  MEMBER_REMOVED: "メンバーを削除",
  INVITATION_REVOKED: "招待を取り消し",
  EXTERNAL_CONNECTED: "取り込み元をつないだ",
  EXTERNAL_DISCONNECTED: "取り込み元を切った",
  SEGMENT_PRODUCT_CONFIRMED: "動画の商品候補を確定",
  SEGMENT_PRODUCT_UNCONFIRMED: "動画の商品候補の確定を取り消し",
  ATTRIBUTE_LEVEL_CHANGED: "属性の扱いを変更",
  ATTRIBUTE_ADDED: "属性を追加",
  KNOWLEDGE_SHEET_UPDATED: "商品知識シートを更新",
  SELLING_ANGLE_ADOPTION_CHANGED: "売り方の提案の採否を変更",
  REGULATION_CHANGED: "レギュレーションブックを変更",
  COLLECT_TASK_CHANGED: "文脈収集の依頼を変更",
  DELEGATION_STARTED: "代行を開始",
  DELEGATION_ENDED: "代行を終了",
  RUN_STOP_POLICY_CHANGED: "実行・停止の権限設定を変更",
  VIDEO_META_BATCH_STARTED: "動画の一括処理を開始",
  VIDEO_META_AUTO_INGEST_CHANGED: "新規動画の自動処理の設定を変更",
  FAQ_DUPLICATES_MERGED: "重なっていた答えを片づけ",
  PERSONA_CHANGED: "ペルソナを変更",
  PERSONA_QUESTION_CHANGED: "ペルソナの観点を変更",
  BRAND_CONTEXT_CHANGED: "ブランドの文脈を変更",
  WRITEBACK_POLICY_CHANGED: "書き戻しの許可範囲を変更",
  WRITEBACK_PROPOSED: "書き戻しを提案",
  WRITEBACK_APPROVED: "書き戻しの提案を承認",
  WRITEBACK_REJECTED: "書き戻しの提案を却下",
  WRITEBACK_APPLIED: "書き戻しを反映",
  WRITEBACK_REVERTED: "書き戻しを元に戻した",
  JOB_ENQUEUED: "処理を開始",
  JOB_FINISHED: "処理が終了",
  PRODUCT_SEMANTICS_FILL_STARTED: "属性の機械生成を開始",
  PRODUCT_SEMANTICS_PIPELINE_STARTED: "属性の機械生成（パイプライン）を開始",
};

/** 絞り込みに出す種別。**よく見るものだけ**を並べる(45 種を全部は並べない)。 */
const FILTERABLE_KINDS: readonly AuditEventKind[] = Object.freeze([
  "PERSONA_CHANGED",
  "PERSONA_QUESTION_CHANGED",
  "BRAND_CONTEXT_CHANGED",
  "KNOWLEDGE_SHEET_UPDATED",
  "SELLING_ANGLE_ADOPTION_CHANGED",
  "REGULATION_CHANGED",
  "QUESTION_TEMPLATE_TOGGLED",
  "QUESTION_TEMPLATE_ADDED",
  "RULE_STATE_CHANGED",
  "RULE_VERSION_SAVED",
  "PUBLISH_TARGET_CHANGED",
  "WRITEBACK_PROPOSED",
  "WRITEBACK_APPROVED",
  "WRITEBACK_REJECTED",
  "FAQ_DUPLICATES_MERGED",
  "JOB_ENQUEUED",
  "JOB_FINISHED",
]);

/**
 * 期間の選択肢。`since` に当てる日数で持つ(0 は「すべて」)。
 *
 * `RANGE` だけは日数ではなく、日付を 2 つ受け取る形に切り替える合図である。
 */
const RANGE = "range";

const PERIODS: readonly { readonly value: string; readonly label: string }[] =
  Object.freeze([
    { value: "7", label: "過去 7 日" },
    { value: "30", label: "過去 30 日" },
    { value: "0", label: "すべて" },
    { value: RANGE, label: "日付で指定する" },
  ]);

/** 1 ページの件数。API の上限は 200 だが、1 画面に載る量にとどめる。 */
const PAGE_SIZE = 50;

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? "その他の操作";
}

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * 期間の起点。**日本時間の日の頭へ丸める**。
 *
 * 丸めないと、描き直すたびに `since` のミリ秒が変わる。urql は変数の**値**で
 * 問い合わせを見分けるので、値が毎回変われば「読み直す → 描き直す →
 * また値が変わる」で止まらなくなる（2026-09-20、CI で実際に
 * 「Too many re-renders」で落ちた。手元は同じミリ秒のうちに描き終わることが
 * 多く、たまたま通っていた）。
 *
 * 丸めるのは表示の都合でもある。「過去 7 日」は、7 日前の**日の頭**から
 * いままで、と読むほうが自然である。
 */
export function auditSinceBoundary(
  days: string,
  now: Date,
): string | undefined {
  const count = Number(days);
  if (!Number.isFinite(count) || count <= 0) return undefined;
  const startOfJstDay =
    Math.floor((now.getTime() + JST_OFFSET_MS) / DAY_MS) * DAY_MS -
    JST_OFFSET_MS;
  return new Date(startOfJstDay - count * DAY_MS).toISOString();
}

/**
 * 日付で指定した期間の境目。**日本時間の 1 日ぶんを、端まで含める**。
 *
 * 開始日はその日の 00:00:00、終了日はその日の 23:59:59.999 に当てる
 * (`filter.until` は「この時刻以前」なので、日の頭に当てると終了日の
 * ほとんどが落ちる)。
 *
 * `now()` を見ないので、描き直しても値が変わらない。urql は変数の値で
 * 問い合わせを見分けるため、ここが毎回変わると読み直しが止まらなくなる
 * (`auditSinceBoundary` の注記と同じ話である)。
 *
 * 空の日付、日付として読めないもの、開始が終了より後ろのものは
 * **当てない**(`undefined` を返す)。半端な範囲で問い合わせて 0 件を返すと、
 * 記録が無いのか条件が壊れているのかが読み手に分からない。
 */
export function auditRangeBoundaries(
  from: string,
  to: string,
): { since?: string; until?: string } | null {
  const since = from === "" ? undefined : jstDayStart(from);
  const until = to === "" ? undefined : jstDayEnd(to);
  if (
    (from !== "" && since === undefined) ||
    (to !== "" && until === undefined)
  )
    return null;
  if (since !== undefined && until !== undefined && since > until) return null;
  return {
    ...(since === undefined ? {} : { since }),
    ...(until === undefined ? {} : { until }),
  };
}

function jstInstant(date: string, time: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(date)) return undefined;
  const at = new Date(`${date}T${time}+09:00`);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

function jstDayStart(date: string): string | undefined {
  return jstInstant(date, "00:00:00.000");
}

function jstDayEnd(date: string): string | undefined {
  return jstInstant(date, "23:59:59.999");
}

interface AuditRow {
  readonly id: string;
  readonly at: string;
  readonly kind: string;
  readonly actorLabel: string;
  readonly onBehalfOf: string | null;
  readonly delegated: boolean;
  readonly resource: string;
  readonly resourceId: string | null;
  readonly summary: string;
  readonly before: unknown;
  readonly after: unknown;
  readonly jobId: string | null;
  /**
   * 事業者へ通知したか。**null は「そもそも通知の対象でない」**。
   *
   * 通知を出すのは代行の開始・終了だけで、残りの種別には送る決まりが無い。
   * 対象でない行に「未送信」を出すと、送り忘れの山に見える。
   */
  readonly notified: boolean | null;
  readonly requestId: string;
}

/** 変更の前後。**初期表示では畳む**(1 行の意味は `summary` で読める)。 */
function ChangeDetail({ row }: { row: AuditRow }) {
  const hasBefore = row.before != null;
  const hasAfter = row.after != null;
  if (!hasBefore && !hasAfter) return null;
  return (
    <Disclosure summary="変わった値を見る">
      <p className="vd-stat-note" style={{ margin: "0 0 8px" }}>
        パスワード・キー・ハッシュ・連絡先は
        <code>***</code> で伏せてあります。
      </p>
      {hasBefore ? (
        <div>
          <div className="vd-stat-note">変更前</div>
          <pre style={{ margin: "0 0 8px", whiteSpace: "pre-wrap" }}>
            {JSON.stringify(row.before, null, 2)}
          </pre>
        </div>
      ) : null}
      {hasAfter ? (
        <div>
          <div className="vd-stat-note">変更後</div>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(row.after, null, 2)}
          </pre>
        </div>
      ) : null}
    </Disclosure>
  );
}

/**
 * 事業者へ通知したかの札。**通知の対象でない行には何も出さない**。
 *
 * 対象は代行の開始・終了だけである。ほかの種別にまで「通知は未送信」を出すと、
 * 送る決まりの無いものが送り忘れに見える。対象の行で送れていないときだけ、
 * 気づける形（warn）で出す。
 */
function NotifiedTag({ notified }: { notified: boolean | null }) {
  if (notified === null) return null;
  return notified ? (
    <Tag tone="ok">事業者へ通知済み</Tag>
  ) : (
    <Tag tone="warn">通知は未送信</Tag>
  );
}

function AuditRowItem({ row }: { row: AuditRow }) {
  const at = formatJstDateTime(row.at);
  return (
    <section className="vd-log-row" aria-label={row.summary}>
      <div className="vd-row" style={{ justifyContent: "space-between" }}>
        <div className="vd-card-title">
          {kindLabel(row.kind)}
          {row.delegated ? <Tag tone="info">visumo が代行</Tag> : null}
          <NotifiedTag notified={row.notified} />
        </div>
        <div className="vd-stat-note">{at}</div>
      </div>
      <p style={{ margin: "8px 0 0" }}>{row.summary}</p>
      <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
        操作した人: {row.actorLabel}
        {row.onBehalfOf === null ? "" : `（${row.onBehalfOf} の代わりに）`}
      </p>
      {/* 畳んである区画どうしが地続きに見えないよう、間を空けて縦に積む */}
      <div className="vd-log-details">
        <ChangeDetail row={row} />
        <Disclosure summary="問い合わせ用の番号">
          <p className="vd-stat-note" style={{ margin: 0 }}>
            受付番号 {row.requestId}
            {row.resourceId === null ? "" : ` / 対象 ${row.resourceId}`}
            {row.jobId === null ? "" : ` / 処理 ${row.jobId}`}
          </p>
        </Disclosure>
      </div>
    </section>
  );
}

export function AuditLogScreen({
  description,
  note,
  help,
  now = () => new Date(),
}: {
  /** 画面の説明。アプリごとに言い方を変える */
  description: string;
  /** 一覧の上に出す断り(オペレーター向けの「いま見ているテナントのぶん」など) */
  note?: ReactNode;
  /** 期間の起点。テストが固定できるようにしてある */
  now?: () => Date;
  /**
   * 見出しの横に置くもの（ヘルプの「?」など）。
   *
   * **この部品はヘルプの中身を知らない。** 文章はアプリの側にあり、
   * オペレーター向けの面には顧客向けの説明を置かない。
   */
  help?: ReactNode;
}) {
  const [kind, setKind] = useState<AuditEventKind | "">("");
  const [period, setPeriod] = useState("30");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  /**
   * いま開いているページの手前までの区切り。
   *
   * 台帳は新しい順に前だけへ進む形で、戻る口が API に無い。**進んだ順に
   * 区切りを積む**ことで戻れるようにしてある(`cursors[n]` が n + 1 ページ目の
   * 起点で、1 ページ目は起点そのものが無い)。
   */
  const [cursors, setCursors] = useState<readonly string[]>([]);

  const range = period === RANGE ? auditRangeBoundaries(from, to) : null;
  const since =
    period === RANGE ? range?.since : auditSinceBoundary(period, now());
  const until = period === RANGE ? range?.until : undefined;
  /** 日付が半端なとき。**問い合わせず**、条件が壊れていることを出す */
  const brokenRange = period === RANGE && range === null;

  const filter = {
    ...(kind === "" ? {} : { kind }),
    ...(since === undefined ? {} : { since }),
    ...(until === undefined ? {} : { until }),
  };
  const after = cursors[cursors.length - 1];

  const [result, refetch] = useQuery({
    query: AuditEventsDocument,
    pause: brokenRange,
    variables: {
      first: PAGE_SIZE,
      ...(after === undefined ? {} : { after }),
      ...(Object.keys(filter).length === 0 ? {} : { filter }),
    },
  });

  /** 絞り込みを変えたら 1 ページ目へ戻す(いまの区切りは別の条件のものである) */
  function restart(apply: () => void): void {
    apply();
    setCursors([]);
  }

  const filters = (
    <div className="vd-filters">
      <label className="vd-filter">
        <span className="vd-field-hint">種別</span>
        <select
          className="vd-select"
          aria-label="種別"
          value={kind}
          onChange={(event) => {
            restart(() => {
              setKind(event.target.value as AuditEventKind | "");
            });
          }}
        >
          <option value="">すべての種別</option>
          {FILTERABLE_KINDS.map((value) => (
            <option key={value} value={value}>
              {kindLabel(value)}
            </option>
          ))}
        </select>
      </label>
      <label className="vd-filter">
        <span className="vd-field-hint">期間</span>
        <select
          className="vd-select"
          aria-label="期間"
          value={period}
          onChange={(event) => {
            restart(() => {
              setPeriod(event.target.value);
            });
          }}
        >
          {PERIODS.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </select>
      </label>
      {period === RANGE ? (
        <>
          <label className="vd-filter">
            <span className="vd-field-hint">開始日</span>
            <input
              className="vd-input"
              type="date"
              aria-label="開始日"
              value={from}
              max={to === "" ? undefined : to}
              onChange={(event) => {
                restart(() => {
                  setFrom(event.target.value);
                });
              }}
            />
          </label>
          <label className="vd-filter">
            <span className="vd-field-hint">終了日</span>
            <input
              className="vd-input"
              type="date"
              aria-label="終了日"
              value={to}
              min={from === "" ? undefined : from}
              onChange={(event) => {
                restart(() => {
                  setTo(event.target.value);
                });
              }}
            />
          </label>
        </>
      ) : null}
    </div>
  );

  const page = cursors.length + 1;

  const body = (): ReactNode => {
    if (brokenRange) {
      // **0 件に倒さない**。条件が半端なのと、記録が無いのは別である
      return (
        <div className="vd-notice" role="alert">
          <p style={{ margin: 0 }}>
            期間を読み取れませんでした。開始日は終了日より前にしてください。
          </p>
        </div>
      );
    }
    if (result.fetching && result.data === undefined) {
      return <LoadingState />;
    }
    if (result.error !== undefined || result.data === undefined) {
      // **0 件に倒さない**。読めなかったことと、記録が無いことは別である
      return (
        <ErrorState
          description={result.error?.message}
          onRetry={() => refetch({ requestPolicy: "network-only" })}
        />
      );
    }
    const rows = result.data.auditEvents.edges.map(
      (edge) => edge.node as AuditRow,
    );
    if (rows.length === 0) {
      return (
        <EmptyState
          title="まだ記録がありません"
          description={
            kind === "" && period === "0"
              ? "この会社ではまだ記録の残る操作が行われていません。"
              : "絞り込みの条件にあてはまる操作はありませんでした。条件を広げると見つかることがあります。"
          }
          action={
            cursors.length === 0 ? undefined : (
              <Button
                onClick={() => {
                  setCursors([]);
                }}
              >
                1 ページ目へ戻る
              </Button>
            )
          }
        />
      );
    }
    const { hasNextPage, endCursor } = result.data.auditEvents.pageInfo;
    return (
      <>
        <div className="vd-log">
          {rows.map((row) => (
            <AuditRowItem key={row.id} row={row} />
          ))}
        </div>
        <div className="vd-pager">
          <Button
            disabled={cursors.length === 0 || result.fetching}
            onClick={() => {
              setCursors((stack) => stack.slice(0, -1));
            }}
          >
            前の {String(PAGE_SIZE)} 件
          </Button>
          {/*
            **「何件中の何ページ」は出せない**。総件数は月ごとの台帳を
            遡る形なので数えていない(`totalCount` は null で返る)。
            いま何ページ目かだけを出す。
          */}
          <span className="vd-stat-note">
            新しい順に {String(PAGE_SIZE)} 件ずつ・{String(page)} ページ目
          </span>
          <Button
            disabled={!hasNextPage || endCursor == null || result.fetching}
            onClick={() => {
              if (endCursor == null) return;
              setCursors((stack) => [...stack, endCursor]);
            }}
          >
            次の {String(PAGE_SIZE)} 件
          </Button>
        </div>
      </>
    );
  };

  return (
    <div className="vd-page">
      <PageHeader
        title="監査ログ"
        description={description}
        titleExtra={help}
      />
      <div className="vd-stack">
        {note === undefined ? null : (
          <div className="vd-notice" role="status">
            {note}
          </div>
        )}
        <Card title="操作の記録">
          {filters}
          {body()}
        </Card>
      </div>
    </div>
  );
}
